import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { select, Store } from '@ngrx/store';
import { CustomerService } from '@services';
import { selectRouteState } from '@state/route';

import {
  fetchCurrentCustomer,
  fetchCustomerById,
  fetchCustomerByIdFailed,
  fetchCustomerByIdSuccess,
  fetchCustomers,
  fetchCustomersFailed,
  fetchCustomersSuccess,
  updateCustomer,
  updateCustomerFailed,
  updateCustomerImage,
  updateCustomerImageFailed,
  updateCustomerImageSuccess,
  updateCustomerSuccess,
} from './customer.actions';

@Injectable()
export class CustomerEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private customerService: CustomerService
  ) {}

  // Read
  fetchCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCustomers),
      switchMap(() =>
        from(this.customerService.get()).pipe(
          map((customers) => fetchCustomersSuccess({ customers })),
          catchError((error) => of(fetchCustomersFailed({ error })))
        )
      )
    )
  );

  fetchCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCustomerById),
      switchMap(({ id }) =>
        from(this.customerService.getById(id)).pipe(
          map((customer) => fetchCustomerByIdSuccess({ customer })),
          catchError((error) => of(fetchCustomerByIdFailed({ error })))
        )
      )
    )
  );

  fetchCurrentCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCurrentCustomer),
      mergeMap(() => this.store.pipe(select(selectRouteState), take(1))),
      switchMap((route) => {
        const customerId = route.params['customerId'];
        return of(fetchCustomerById({ id: customerId }));
      })
    )
  );

  // Update
  updateCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCustomer),
      switchMap(({ customer }) =>
        from(this.customerService.update(customer)).pipe(
          map((customer) => updateCustomerSuccess({ customer })),
          catchError((error) => of(updateCustomerFailed({ error })))
        )
      )
    )
  );

  updateCustomerImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCustomerImage),
      switchMap(({ customer, file }) =>
        from(this.customerService.updateImage(file, customer)).pipe(
          map((customer) => updateCustomerImageSuccess({ customer })),
          catchError((error) => of(updateCustomerImageFailed({ error })))
        )
      )
    )
  );
}
